import { useEffect, useMemo, useState } from 'react';
import { Check, RefreshCw, ImageIcon, Send, AlertCircle, Users, Replace, Trash2 } from 'lucide-react';
import type { Campaign, Decision, SceneReview } from '../../../shared/types';
import { useCollection } from '../lib/hooks';
import { saveDecision, submitReview, replaceReviewImage, deleteScene, errorText } from '../lib/api';
import type { AuthUser } from '../lib/pb';
import { driveThumbnailUrl } from '../lib/drive';
import TrimPlayer from './TrimPlayer';
import ImageLibrary from './ImageLibrary';
import { confirmDialog } from './ConfirmDialog';

/**
 * Image and video review.
 *
 * Decisions are one field on one record. v1 needed a write-order protocol here:
 * the note and prompt edit had to reach columns G:I *before* the decision in
 * column F, because n8n polled for a non-blank decision cell and would otherwise
 * act on a half-written verdict.
 */
export default function ReviewSection({
  campaign,
  kind,
  user,
}: {
  campaign: Campaign;
  kind: 'image' | 'video';
  user: AuthUser;
}) {
  const reviews = useCollection<SceneReview>('scene_reviews', {
    filter: `campaign = "${campaign.id}" && kind = "${kind}"`,
    sort: 'scene_index',
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rendering = campaign.stage === 'images_rendering' || campaign.stage === 'videos_rendering';

  const decided = reviews.items.filter((r) => r.decision).length;
  const total = reviews.items.length;
  const allDecided = total > 0 && decided === total;

  // Who else has recorded a verdict this round — several people review at once,
  // and the subscription means their decisions land here live.
  const otherReviewers = useMemo(() => {
    const names = new Set<string>();
    for (const r of reviews.items) {
      if (r.decided_by && r.decided_by !== user.id) names.add(r.decided_by);
    }
    return names.size;
  }, [reviews.items, user.id]);

  if (rendering) {
    const decided = reviews.items.filter((r) => r.decision);
    return (
      <div className="space-y-4">
        <div className="zen-card p-10 text-center space-y-4">
          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-natural-bg">
            <span className="text-3xl">{kind === 'image' ? '🖼️' : '🎬'}</span>
            <div className="absolute inset-0 rounded-full border-2 border-dashed border-natural-sage/40 animate-spin-slower" />
          </div>
          <div className="space-y-1.5">
            <p className="font-serif text-lg font-medium">
              {campaign.stage_message || `Rendering ${kind === 'image' ? 'images' : 'video'}…`}
            </p>
            <p className="text-xs text-natural-text/55">
              Scenes appear here as they finish. Nothing is lost if you leave.
            </p>
          </div>
        </div>

        {/* The verdicts that produced this render, read-only — so anyone can
            still see what was decided while the work is in flight. */}
        {decided.length > 0 && (
          <details className="zen-card p-5">
            <summary className="text-xs font-semibold text-natural-text/60 cursor-pointer hover:text-natural-sage select-none">
              View submitted decisions ({decided.length})
            </summary>
            <div className="mt-3 space-y-2">
              {decided.map((r) => (
                <div
                  key={r.id}
                  className="flex items-start gap-3 p-2.5 rounded-xl bg-natural-bg/60 border border-natural-border/60 text-xs"
                >
                  <span className="font-mono text-[10px] text-natural-text/45 mt-0.5 shrink-0">
                    SCENE {r.scene_index + 1}
                  </span>
                  <span
                    className={`zen-pill shrink-0 ${
                      r.decision === 'approve'
                        ? 'bg-emerald-50 text-emerald-700'
                        : r.decision === 'rerender_image'
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-rose-50 text-rose-700'
                    }`}
                  >
                    {r.decision === 'approve'
                      ? 'Approved'
                      : r.decision === 'rerender_image'
                        ? 'Re-render image'
                        : 'Re-render'}
                  </span>
                  <div className="min-w-0 text-natural-text/60 leading-relaxed">
                    {r.note && <p className="truncate">“{r.note}”</p>}
                    {r.prompt_edit && (
                      <p className="truncate text-natural-text/45">edit: {r.prompt_edit}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    );
  }

  if (reviews.loading) {
    return <div className="zen-card p-10 text-center text-xs text-natural-text/45">Loading…</div>;
  }

  if (!total) {
    return (
      <div className="zen-card p-10 text-center text-xs text-natural-text/50">
        Nothing to review yet.
      </div>
    );
  }

  const submit = async () => {
    // Every path that spends money says exactly what it will generate first.
    const items = reviews.items;
    const rerenders = items.filter((r) => r.decision === 'rerender').length;
    const imageRerenders = items.filter((r) => r.decision === 'rerender_image').length;

    let message: string;
    if (kind === 'image') {
      const n = rerenders + imageRerenders;
      message =
        n > 0
          ? `This will re-render ${n} image(s).`
          : `All ${items.length} image(s) approved — this will generate ${items.length} video clip(s), the most expensive step.`;
    } else {
      const clips = rerenders + imageRerenders;
      message =
        clips > 0
          ? `This will generate ${
              imageRerenders ? `${imageRerenders} new image(s) and ` : ''
            }${clips} new video clip(s).`
          : `All clips approved — the final video will be assembled and published. No generation cost.`;
    }
    if (!(await confirmDialog({ title: 'Submit decisions?', message, confirmLabel: 'Submit' }))) {
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await submitReview(campaign.id, kind);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="zen-card p-5">
        <h3 className="font-serif text-lg font-medium">
          {kind === 'image' ? 'Image review' : 'Video review'}
        </h3>
        <p className="text-xs text-natural-text/55 mt-0.5">
          {decided} of {total} scenes decided
          {otherReviewers > 0 && (
            <span className="inline-flex items-center gap-1 ml-2 text-natural-sage">
              <Users size={11} />
              {otherReviewers} other reviewer{otherReviewers > 1 ? 's' : ''} active
            </span>
          )}
        </p>
      </div>

      {kind === 'video' && campaign.parent_campaign && (
        <div className="zen-card p-4 text-xs leading-relaxed border-natural-sage/40 bg-natural-sage/5">
          <p className="font-semibold text-natural-sage mb-0.5">
            Revision of #{campaign.parent_seq}
          </p>
          <p className="text-natural-text/70">
            Every clip from the finished video is here and already approved. Change only the
            scene(s) that are wrong to <strong>Re-render</strong> (new clip, same image) or{' '}
            <strong>Re-render image</strong> (new image, then new clip), add a note about what to
            fix, and submit. Only those scenes are generated again; the rest are reused as they
            are, and a new final video is assembled.
          </p>
        </div>
      )}

      {kind === 'video' && (
        <p className="text-[11px] text-natural-text/50 px-1 leading-relaxed">
          <strong>Re-render video</strong> makes a new clip from the same image.{' '}
          <strong>Re-render image</strong> makes a new image first, then a new clip. You can mix
          both in one pass — they are processed together in a single round.
        </p>
      )}

      <div className="space-y-4">
        {reviews.items.map((review) => (
          <ReviewCard
            key={review.id}
            review={review}
            kind={kind}
            campaign={campaign}
            lastScene={reviews.items.length <= 1}
            onSaved={() => void reviews.reload()}
          />
        ))}
      </div>

      {error && (
        <div className="zen-card p-4 text-xs text-red-600 flex items-start gap-2">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Submit sits below the cards: decisions are made top-to-bottom, so the
          button is where the reviewer's eyes are when they finish the last one. */}
      <div className="zen-card p-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-natural-text/55">
          {allDecided
            ? 'All scenes decided.'
            : `${decided} of ${total} scenes decided — every scene needs a decision before submitting.`}
        </p>
        <button onClick={submit} disabled={!allDecided || busy} className="zen-btn-primary">
          <Send size={14} />
          {busy ? 'Submitting…' : 'Submit decisions'}
        </button>
      </div>
    </div>
  );
}

function ReviewCard({
  review,
  kind,
  campaign,
  lastScene,
  onSaved,
}: {
  review: SceneReview;
  kind: 'image' | 'video';
  campaign: Campaign;
  lastScene: boolean;
  onSaved: () => void;
}) {
  // `||` not `??`: PocketBase returns 0 (not null) for unset number fields, and
  // an out-point of 0 would make the clip unplayable.
  const [decision, setDecision] = useState<Decision>(review.decision);
  const [note, setNote] = useState(review.note);
  const [promptEdit, setPromptEdit] = useState(review.prompt_edit);
  const [inPoint, setInPoint] = useState(review.in_point || 0.01);
  const [outPoint, setOutPoint] = useState(review.out_point || 7.85);
  const [holding, setHolding] = useState<'' | 'YES' | 'NO'>(review.holding_product ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);

  // The provider's CDN link is what gets stored, because it plays inline and
  // ffmpeg/kie can fetch it. Those links expire, so a campaign revisited days
  // later falls back to the Drive copy rather than showing a broken player.
  const [mediaFailed, setMediaFailed] = useState(false);
  const mediaSrc = mediaFailed && review.drive_url ? review.drive_url : review.media_url;

  const replaceWithExisting = async (url: string) => {
    setLibraryOpen(false);
    setSaving(true);
    setError(null);
    try {
      // Swaps the image and approves it in one write — "this one works, don't
      // spend credits re-rendering". The realtime refetch shows the new image.
      await replaceReviewImage(review.id, url);
      onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  // Reset when the worker replaces this scene's media in a new round.
  useEffect(() => {
    setDecision(review.decision);
    setNote(review.note);
    setPromptEdit(review.prompt_edit);
    setInPoint(review.in_point || 0.01);
    setOutPoint(review.out_point || 7.85);
    setHolding(review.holding_product ?? '');
    setMediaFailed(false);
  }, [review.id, review.media_url, review.review_round, review.decision, review.note, review.prompt_edit, review.in_point, review.out_point, review.holding_product]);

  const commit = async (
    next: Decision,
    points?: { inPoint: number; outPoint: number },
    holdingNext?: '' | 'YES' | 'NO'
  ) => {
    // The override only travels with a decision that re-renders the image; on
    // approve the row's holding_product stays the label of what was generated.
    const rerendersImage = kind === 'image' ? next === 'rerender' : next === 'rerender_image';
    setSaving(true);
    setError(null);
    setDecision(next);
    try {
      await saveDecision({
        id: review.id,
        decision: next,
        note,
        promptEdit,
        ...(rerendersImage ? { holdingProduct: holdingNext ?? holding } : {}),
        ...(kind === 'video'
          ? { inPoint: points?.inPoint ?? inPoint, outPoint: points?.outPoint ?? outPoint }
          : {}),
      });
      onSaved();
    } catch (err) {
      setError(errorText(err));
      setDecision(review.decision);
    } finally {
      setSaving(false);
    }
  };

  const removeScene = async () => {
    const ok = await confirmDialog({
      title: `Delete scene ${review.scene_index + 1}?`,
      message:
        `The scene, its ${kind === 'video' ? 'video and image' : 'image'} and its line of script` +
        (review.speech ? ` (“${review.speech}”)` : '') +
        ` will be removed from the campaign entirely. It will not appear in the final video or its captions.\n\nThis cannot be undone.`,
      confirmLabel: 'Delete scene',
      danger: true,
    });
    if (!ok) return;

    setSaving(true);
    setError(null);
    try {
      await deleteScene(campaign.id, review.scene_index);
      onSaved();
    } catch (err) {
      setError(errorText(err));
      setSaving(false);
    }
    // On success the card unmounts via the realtime delete / reload — no state
    // reset needed (and setting it would warn about updates after unmount).
  };

  const wantsRerender = decision === 'rerender' || decision === 'rerender_image';

  return (
    <div
      className={`zen-card p-5 space-y-4 transition-colors ${
        review.failed ? 'border-red-200' : decision ? 'border-natural-sage/40' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] text-natural-text/45">
          SCENE {review.scene_index + 1}
          {review.review_round > 0 && (
            <span className="ml-2 text-natural-sage">round {review.review_round + 1}</span>
          )}
        </span>
        <div className="flex items-center gap-2">
          {saving && <span className="text-[10px] text-natural-text/40">Saving…</span>}
          <button
            onClick={() => void removeScene()}
            disabled={saving || lastScene}
            title={
              lastScene
                ? 'The last scene cannot be deleted — a campaign needs at least one scene.'
                : 'Delete this scene from the campaign entirely'
            }
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold
                       text-red-600 border border-red-200 bg-white hover:bg-red-50
                       transition-colors cursor-pointer
                       disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none"
          >
            <Trash2 size={12} />
            Delete scene
          </button>
        </div>
      </div>

      {review.failed ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">
          <p className="font-semibold mb-1">This scene failed to render</p>
          <p className="leading-relaxed">{review.fail_reason || 'No reason was recorded.'}</p>
          <p className="mt-2 text-[11px] text-red-600/80">
            Choose re-render below to try it again. The other scenes are unaffected.
          </p>
        </div>
      ) : review.media_expired ? (
        <div className="p-4 bg-stone-100 border border-natural-border rounded-xl text-xs text-natural-text/60 flex items-center gap-2">
          <ImageIcon size={14} />
          This media came from an old campaign and its link has expired.
        </div>
      ) : kind === 'image' ? (
        <img
          // Drive links (uc?export=download …) don't render in an <img> tag;
          // route them through the thumbnail endpoint at w1000 for the big
          // view. Non-Drive URLs (kie.ai CDN) pass through untouched.
          src={driveThumbnailUrl(mediaSrc, 'w1000')}
          alt={`Scene ${review.scene_index + 1}`}
          loading="lazy"
          onError={() => setMediaFailed(true)}
          className="w-full rounded-2xl border border-natural-border bg-stone-100"
        />
      ) : (
        <TrimPlayer
          src={mediaSrc}
          onError={() => setMediaFailed(true)}
          inPoint={inPoint}
          outPoint={outPoint}
          onChange={(next) => {
            setInPoint(next.inPoint);
            setOutPoint(next.outPoint);
            // A trim adjusted after the decision was clicked must still be
            // saved — assembly cuts to what's in the database, not this state.
            if (decision) void commit(decision, next);
          }}
        />
      )}

      {review.speech && (
        <p className="text-xs text-natural-text/70 italic leading-relaxed px-1">
          “{review.speech}”
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => commit('approve')}
          disabled={saving}
          className={`zen-btn flex-1 min-w-[120px] ${
            decision === 'approve'
              ? 'bg-natural-sage text-white'
              : 'bg-white border border-natural-border hover:border-natural-sage/50'
          }`}
        >
          <Check size={14} />
          Approve
        </button>

        <button
          onClick={() => commit('rerender')}
          disabled={saving}
          className={`zen-btn flex-1 min-w-[120px] ${
            decision === 'rerender'
              ? 'bg-natural-rosetext text-white'
              : 'bg-white border border-natural-border hover:border-natural-rosetext/50'
          }`}
        >
          <RefreshCw size={14} />
          {kind === 'image' ? 'Re-render' : 'Re-render video'}
        </button>

        {kind === 'video' && (
          <button
            onClick={() => commit('rerender_image')}
            disabled={saving}
            className={`zen-btn flex-1 min-w-[120px] ${
              decision === 'rerender_image'
                ? 'bg-amber-600 text-white'
                : 'bg-white border border-natural-border hover:border-amber-500/50'
            }`}
          >
            <ImageIcon size={14} />
            Re-render image
          </button>
        )}

        {kind === 'image' && (
          <button
            onClick={() => setLibraryOpen(true)}
            disabled={saving}
            className="zen-btn flex-1 min-w-[120px] bg-white border border-natural-border hover:border-natural-sage/50"
            title="Swap in a previously approved image instead of re-rendering — costs nothing"
          >
            <Replace size={14} />
            Use existing
          </button>
        )}
      </div>

      {libraryOpen && (
        <ImageLibrary
          influencerId={campaign.influencer}
          productId={campaign.product}
          locationTag={review.location_tag}
          holdingProduct={review.holding_product}
          excludeId={review.id}
          onPick={(pick) => void replaceWithExisting(pick.url)}
          onClose={() => setLibraryOpen(false)}
        />
      )}

      {wantsRerender && (kind === 'image' ? decision === 'rerender' : decision === 'rerender_image') && (
        <div>
          <label className="zen-label">
            Product in hand
            <span className="ml-1.5 font-normal normal-case tracking-normal text-natural-text/40">
              overrides the script's setting for this re-render
            </span>
          </label>
          <div className="flex gap-2 mt-1">
            {(['YES', 'NO'] as const).map((value) => (
              <button
                key={value}
                disabled={saving}
                onClick={() => {
                  const next = holding === value ? '' : value;
                  setHolding(next);
                  if (decision) void commit(decision, undefined, next);
                }}
                className={`zen-btn text-xs px-4 ${
                  holding === value
                    ? 'bg-natural-sage text-white'
                    : 'bg-white border border-natural-border hover:border-natural-sage/50'
                }`}
              >
                {value === 'YES' ? 'Holding product' : 'No product'}
              </button>
            ))}
          </div>
        </div>
      )}

      {wantsRerender && (
        <div className="space-y-3 pt-1">
          <div>
            <label className="zen-label">
              What should change?
              {/* v1 collected this field and passed it all the way into the retry
                  payload as `userNote`, where no prompt builder ever read it. */}
              <span className="ml-1.5 font-normal normal-case tracking-normal text-natural-text/40">
                sent to the model as a correction
              </span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={() => decision && void commit(decision)}
              rows={2}
              placeholder="e.g. her hand is cut off at the wrist"
              className="zen-input text-xs resize-y"
            />
          </div>

          <details>
            <summary className="text-[11px] text-natural-text/50 cursor-pointer hover:text-natural-sage select-none">
              Replace the {kind === 'image' ? 'scene description' : 'direction'} entirely
            </summary>
            <textarea
              value={promptEdit}
              onChange={(e) => setPromptEdit(e.target.value)}
              onBlur={() => decision && void commit(decision)}
              rows={3}
              placeholder={
                kind === 'image'
                  ? 'Full replacement for the scene description'
                  : 'Full replacement for the camera and performance direction'
              }
              className="zen-input text-xs resize-y mt-2"
            />
          </details>
        </div>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
